/**
 * MonsterInfo — full + movement artwork for a monster species (sidebar detail panel).
 * Shared by MonsterList and MonsterDeduction.
 */

import React, { useCallback } from 'react';
import { getMonsterNames } from '../../utils/constants';
import { getMonster } from '../../../utils/images';
import type { Theme } from '../../types/game.types';
import type { MonsterTypeId, MonsterName } from '../../types/monster.types';
import './MonsterList.css';

export interface MonsterInfoSubject {
    type: MonsterTypeId;
    name: MonsterName;
}

export interface MonsterInfoProps {
    theme: Theme;
    monster: MonsterInfoSubject | null;
    /** First part of the title line, e.g. "Selected" → "Selected: Leviathan" */
    titleLabel?: string;
    emptyMessage?: string;
    /** Movement diagram orientation; parent owns state and passes toggle. */
    imageDirection: 'up' | 'down';
    onToggleImageDirection: () => void;
}

export const MonsterInfo: React.FC<MonsterInfoProps> = ({
    theme,
    monster,
    titleLabel = 'Selected',
    emptyMessage = 'Select a monster above to place on the board',
    imageDirection,
    onToggleImageDirection,
}) => {
    const getMovementImageUrl = useCallback(
        (typeId: MonsterTypeId): string => {
            const info = getMonsterNames(typeId);
            const url = info?.url;
            const filename = `${url}_moves.png`;
            return getMonster(theme, filename);
        },
        [theme]
    );

    const getFullImageUrl = useCallback(
        (typeId: MonsterTypeId): string => {
            const info = getMonsterNames(typeId);
            const url = info?.url;
            const filename = `${url}_full.png`;
            return getMonster(theme, filename);
        },
        [theme]
    );

    return (
        <div className="monster_info_container">
            <div className="monster_info_title">
                <p className="monster_info_title_text">
                    {titleLabel}: {monster?.name ?? 'None'}
                </p>

                <button
                    type="button"
                    className="monster_info_direction_button"
                    onClick={onToggleImageDirection}
                >
                    {imageDirection === 'up' ? '▲' : '▼'}
                </button>
            </div>

            <div className="monster_info_remaining">
                {monster ? (
                    <>
                        <div className="monster_info_image_square_container">
                            <img
                                className="monster_info_image"
                                src={getFullImageUrl(monster.type)}
                                alt={`${monster.name} full`}
                            />
                        </div>
                        <div className="monster_info_movement_square_container">
                            <img
                                className="monster_info_movement_image"
                                src={getMovementImageUrl(monster.type)}
                                alt={`${monster.name} movement`}
                                style={{
                                    transform: imageDirection === 'down' ? 'rotate(180deg)' : undefined,
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <div className="monster_info_image_square_container">
                        <p className="monster_info_info_text">{emptyMessage}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonsterInfo;
